import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { getBetsTranslation } from 'src/domains/layouts/shared/translations/bets';
import {
    BetSelectionHeader,
    BetSelectionHeaderEventName,
    ArrowBetSelectionWrapper,
    BetType,
    BetElementList,
} from 'src/domains/sportsbook/betting/bettingTab/cashout/selection/Selection.style';
import { LegsInBet } from './legsInBet/LegsInBet';
import { BetSummary } from './betSummary/BetSummary';
import { BogMessageComponent } from './BogMessageComponent';
import { SettledCashOut } from './SettledCashout';
import { CashOutButtons } from 'src/domains/sportsbook/betting/bettingTab/cashout/selection/selectionNew/CashoutButtons';
import { SingleBetItemState } from 'src/domains/sportsbook/betting/betSlipState/betsState/SingleBetItemState';
import { computed, makeObservable } from 'mobx';
import { BetsFilter } from 'src/domains/sportsbook/betting/state/BetsState';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';

interface PropsTypesSelectionType {
    betId: number | null;
}

class SelectionState {
    private readonly bet: SingleBetItemState;

    public constructor(bet: SingleBetItemState) {
        makeObservable(this);
        this.bet = bet;
    }

    @computed public get checkStatusSettled(): boolean {
        return this.bet.status === 'settled';
    }

    @computed public get checkIfBetIsWon(): boolean {
        return this.bet.payout !== null && this.bet.payout.isGreaterThanZero();
    }
}

export const Selection = observer('Selection', ({ betId }: PropsTypesSelectionType) => {
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);
    const appSportsBookState = AppSportsBookState.get(common);
    const { betsState } = appSportsBookState;

    if (betId === null) {
        return null;
    }

    const { getBetItemById } = betsState;
    const betItem = getBetItemById(betId);
    const [state] = useState(() => new SelectionState(betItem));
    const { isSelectionOpen, toggleSelectionOpen, cashOutErrors, id, type, legs } = betItem;

    if (id === null) {
        return null;
    }
    const isBetGlider = type === 'BB' && legs?.[0]?.sport?.id === 'horseracing';

    const betType =
        type === null
            ? null
            : isBetGlider
              ? 'Single - BG'
              : getBetsTranslation(languagesState, configState, type) ?? null;

    return (
        <BetElementList
            hasStatus={state.checkStatusSettled}
            winingBet={state.checkIfBetIsWon}
            data-test='bet-element'
            data-test-bet-type={betType}
            variant={configState.componentVariants.betslipVariant}
        >
            {betType?.toLowerCase() === 'single' ? null : (
                <BetSelectionHeader>
                    <ArrowBetSelectionWrapper
                        position={isSelectionOpen ? 'down' : 'right'}
                        onClick={toggleSelectionOpen}
                    />
                    <BetType data-test='bet-type'>{betType}</BetType>
                    {type === 'RAB' || type === 'BB' ? (
                        <BetSelectionHeaderEventName data-test='rab-event-name'>
                            {betItem.legs[0]?.event.name ?? configState.rabHeaderTitle}
                        </BetSelectionHeaderEventName>
                    ) : null}
                </BetSelectionHeader>
            )}

            {isSelectionOpen ? (
                <>
                    <LegsInBet betItem={betItem} />
                    <BetSummary betItem={betItem} />
                    <BogMessageComponent betItem={betItem} />
                    <SettledCashOut betItem={betItem} />
                    {betsState.selectedFilter === BetsFilter.CASH_OUT ? <CashOutButtons betItem={betItem} /> : null}
                    {cashOutErrors.length > 0 ? (
                        <div className='bet__errors'>
                            {cashOutErrors.map((elem) => (
                                <Messages
                                    type='error'
                                    marginBottom='16px'
                                    key={elem}
                                    message={elem}
                                />
                            ))}
                        </div>
                    ) : null}
                </>
            ) : null}
        </BetElementList>
    );
});
